import type { InfiniteData, UseSuspenseInfiniteQueryResult } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";

/**
 * "Show more" under a list paged by an infinite query (0084): reads the next
 * page while there is one, and the rows of the new page join the list. Home
 * and the threads, which keep their pages in the component, draw their own.
 */
export function ShowMore({
  query,
}: {
  query: Pick<UseSuspenseInfiniteQueryResult, "hasNextPage" | "isFetchingNextPage" | "fetchNextPage">;
}) {
  if (!query.hasNextPage) return null;
  return (
    <div className="text-center">
      <Button
        type="button"
        variant="outline"
        size="lg"
        disabled={query.isFetchingNextPage}
        onClick={() => void query.fetchNextPage()}
      >
        Show more
      </Button>
    </div>
  );
}

/**
 * Every row of the pages read so far, each once: a page read again after a
 * change may have moved a row from one page to the next.
 */
export function rowsOf<Page, Row>(
  data: InfiniteData<Page, unknown>,
  rows: (page: Page) => Row[],
  key: (row: Row) => string,
): Row[] {
  const seen = new Set<string>();
  return data.pages.flatMap(rows).filter((row) => {
    const id = key(row);
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}
